import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { apiFetch, apiFetchEmpty } from './client';
import { keys } from './keys';
import { CommunitiesListSchema, CommunityRowSchema } from './schemas';

export function useCommunities(builderId: string | undefined) {
  return useQuery({
    queryKey: keys.communities(builderId ?? ''),
    enabled: !!builderId,
    queryFn: () => apiFetch(`/api/builders/${builderId}/communities`, CommunitiesListSchema),
  });
}

export function useCommunity(communityId: string | undefined) {
  return useQuery({
    queryKey: keys.community(communityId ?? ''),
    enabled: !!communityId,
    queryFn: () => apiFetch(`/api/communities/${communityId}`, CommunityRowSchema),
  });
}

export function useCreateCommunity(builderId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      name: string;
      num_lots?: number | null;
      city?: string | null;
      zip?: string | null;
      scope_ids?: number[];
      suppliers?: Record<string, string>;
    }) =>
      apiFetch(`/api/builders/${builderId}/communities`, z.object({ id: z.coerce.number(), name: z.string() }), {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.communities(builderId ?? '') });
      void qc.invalidateQueries({ queryKey: keys.builders() });
    },
  });
}

export function useDeleteCommunity(builderId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => apiFetchEmpty(`/api/communities/${id}`, { method: 'DELETE' }),
    onSuccess: (_data, id) => {
      void qc.invalidateQueries({ queryKey: keys.builders() });
      void qc.invalidateQueries({ queryKey: keys.community(id) });
      if (builderId) {
        void qc.invalidateQueries({ queryKey: keys.communities(builderId) });
      }
    },
  });
}

export function useUpdateCommunity(communityId: string | undefined, builderId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      name: string;
      num_lots?: number | null;
      city?: string | null;
      zip?: string | null;
      scope_ids?: number[];
      quote_footer_html?: string;
      sage_id?: string | null;
    }) =>
      apiFetchEmpty(`/api/communities/${communityId}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.community(communityId ?? '') });
      void qc.invalidateQueries({ queryKey: keys.communities(builderId ?? '') });
    },
  });
}
